import React, { useState, useRef, useEffect } from 'react';
import { Dropdown } from 'react-bootstrap';
import CenteredModal from '../../components/widgets/modal/Modal';
import OperationInscriptionModalContent from './OperationInscriptionModalContent';
import OperationInscriptionEditForm from './OperationInscriptionEditForm';
import axios from '../../services/axios';

const buildReference = (slug, id) =>
    `M360-${slug.toUpperCase()}-${String(id).padStart(4, '0')}`;

const OperationInscriptionModal = ({
    inscriptionId,
    filteredItems,
    onClose,
    onNavigate,
    onStatusChanged,
    onDeleted,
    onTagChanged,
    availableTagOperations,
    ACTIONS,
    slug,
    inscriptionStatus,
}) => {
    const [mode, setMode] = useState('view');
    const [isSending, setIsSending] = useState(false);
    const [loadedInscription, setLoadedInscription] = useState(null);
    const editSubmitRef = useRef(null);

    const inscriptionStatusLabel = Object.fromEntries(inscriptionStatus.map(s => [s.value, s.label]));
    const inscriptionStatusVariant = Object.fromEntries(inscriptionStatus.map(s => [s.value, s.variant ?? 'secondary']));
    const inscriptionStatusByValue = Object.fromEntries(inscriptionStatus.map(s => [s.value, s]));

    useEffect(() => {
        setMode('view');
        setLoadedInscription(null);
    }, [inscriptionId]);

    const currentIndex = filteredItems.findIndex(item => item.id === inscriptionId);
    const inscription = filteredItems[currentIndex];

    if (!inscription) return null;

    const canEdit = inscriptionStatusByValue[inscription.statut]?.updatable !== false;

    const reference = buildReference(slug, inscription.id);
    const detailUrl = ACTIONS?.operations?.getSubEntities?.inscriptions?.readOne?.url
        ?.replace('/id', `/${inscriptionId}`);

    const handleStatusChange = (newStatut) => {
        if (!canEdit) return;
        const action = ACTIONS?.operations?.getSubEntities?.inscriptions?.updateStatus;
        const url = action?.url?.replace('/id', `/${inscription.id}`);
        if (!url) return;
        setIsSending(true);
        axios.post(url, { csrfToken: action.csrfToken, statut: newStatut })
            .then(() => onStatusChanged(inscription.id, newStatut))
            .finally(() => setIsSending(false));
    };

    const handleDelete = () => {
        if (!canEdit) return;
        const action = ACTIONS?.operations?.getSubEntities?.inscriptions?.delete;
        const url = action?.url?.replace('/id', `/${inscription.id}`);
        if (!url) return;
        setIsSending(true);
        axios.delete(url, { data: { csrfToken: action.csrfToken } })
            .then(() => onDeleted(inscription.id))
            .finally(() => setIsSending(false));
    };

    const plainHeader = (
        <>
            {inscription.etablissementNom}
            {inscription.uai && <small className="ms-2 text-muted">{inscription.uai}</small>}
            <small className="ms-2 text-muted">{reference}</small>
        </>
    );

    const viewHeader = (
        <div className="d-flex w-100 align-items-center justify-content-between me-3">
            <div>
                {inscription.etablissementNom}
                {inscription.uai && <small className="ms-2 text-muted">{inscription.uai}</small>}
                <small className="ms-2 text-muted">{reference}</small>
            </div>
            <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" size="sm" id="inscription-statut-select" disabled={!canEdit}>
                    <span
                        className={`rounded-circle bg-${inscriptionStatusVariant[inscription.statut] ?? 'secondary'} me-1`}
                        style={{ width: 10, height: 10, display: 'inline-block', verticalAlign: 'middle' }}
                    />
                    {inscriptionStatusLabel[inscription.statut] ?? inscription.statut}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {inscriptionStatus.filter(s => s.updatable !== false).map(s => (
                        <Dropdown.Item
                            key={s.value}
                            active={s.value === inscription.statut}
                            onClick={() => handleStatusChange(s.value)}
                        >
                            <span
                                className={`rounded-circle bg-${inscriptionStatusVariant[s.value] ?? 'secondary'} me-2`}
                                style={{ width: 10, height: 10, display: 'inline-block', verticalAlign: 'middle' }}
                            />
                            {s.label}
                        </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );

    const commonProps = { show: true, size: 'xl', isSending, onHide: onClose };

    if (mode === 'delete') {
        return (
            <CenteredModal
                {...commonProps}
                header={plainHeader}
                content={
                    <p>Êtes-vous sûr de vouloir supprimer l&apos;inscription de <strong>{inscription.etablissementNom}</strong> ({reference}) ?</p>
                }
                cancelbutton="Annuler"
                cancel={() => setMode('view')}
                resetButton="Supprimer"
                reset={handleDelete}
                resetFirst
            />
        );
    }

    if (mode === 'edit') {
        const updateAction = ACTIONS?.operations?.getSubEntities?.inscriptions?.update;
        return (
            <CenteredModal
                {...commonProps}
                header={plainHeader}
                content={
                    <OperationInscriptionEditForm
                        inscription={loadedInscription}
                        updateUrl={updateAction?.url?.replace('/id', `/${inscription.id}`)}
                        csrfToken={updateAction?.csrfToken}
                        availableTagOperations={availableTagOperations}
                        onSaved={(data) => {
                            setMode('view');
                            if (data?.tagOperation !== undefined) {
                                onTagChanged?.(inscription.id, data.tagOperation);
                            }
                        }}
                        onSubmitReady={(fn) => { editSubmitRef.current = fn; }}
                        setIsSending={setIsSending}
                    />
                }
                validatebutton="Enregistrer"
                validate={() => editSubmitRef.current?.()}
                cancelbutton="Annuler"
                cancel={() => setMode('view')}
            />
        );
    }

    return (
        <CenteredModal
            {...commonProps}
            header={viewHeader}
            content={
                <OperationInscriptionModalContent
                    key={inscriptionId}
                    detailUrl={detailUrl}
                    currentIndex={currentIndex}
                    total={filteredItems.length}
                    onNavigate={(newIndex) => onNavigate(filteredItems[newIndex].id)}
                    onInscriptionLoaded={setLoadedInscription}
                    availableTagOperations={availableTagOperations}
                />
            }
            cancelbutton={canEdit ? 'Éditer' : undefined}
            cancel={canEdit ? () => loadedInscription && setMode('edit') : onClose}
            resetButton={canEdit ? 'Supprimer' : undefined}
            reset={canEdit ? () => setMode('delete') : undefined}
            resetFirst
        />
    );
};

export default OperationInscriptionModal;
